import {
  buildPlaceholderMatchers,
  placeholderRange
} from "@/zapi-frontend/widgets/CreateProjectChat/lib/templatePlaceholders";
import {
  defaultCaseBrief,
  defaultCaseBriefJapanese,
  retiredTemplateLines
} from "@/zapi-frontend/widgets/CreateProjectChat/const/defaultCaseBrief";
import type { CaseBriefCompleteness } from "@/zapi-frontend/widgets/CreateProjectChat";

const SECTION_START = /\n(?=## \d+\.)/;
const HEADING = /^## (\d+)\.\s*(.*)$/;

export type TCaseFrameSection = { number: number; title: string; heading: string; body: string; trailing: string };
type TCaseFramePart = { section: TCaseFrameSection } | { raw: string };

// The brief split at its "## N." headings; text outside the sections is kept as it is.
export function splitCaseFrame(brief: string): TCaseFramePart[] {
  return brief.split(SECTION_START).map((part) => {
    const newline = part.indexOf("\n");
    const heading = newline === -1 ? part : part.slice(0, newline);
    const match = heading.match(HEADING);
    if (!match) return { raw: part };
    const rest = newline === -1 ? "" : part.slice(newline + 1);
    const trailing = rest.match(/\n*$/)?.[0] ?? "";
    return {
      section: {
        number: Number(match[1]),
        title: match[2].trim(),
        heading,
        body: rest.slice(0, rest.length - trailing.length),
        trailing
      }
    };
  });
}

export function caseFrameSections(brief: string): TCaseFrameSection[] {
  return splitCaseFrame(brief).flatMap((part) => ("section" in part ? [part.section] : []));
}

export function withSectionBody(brief: string, number: number, body: string): string {
  return splitCaseFrame(brief)
    .map((part) => {
      if ("raw" in part) return part.raw;
      const { section } = part;
      if (section.number !== number) return `${section.heading}\n${section.body}${section.trailing}`;
      return `${section.heading}\n${body.replace(/\n+$/, "")}${section.trailing || "\n"}`;
    })
    .join("\n");
}

const templateMatchers = buildPlaceholderMatchers([defaultCaseBrief, defaultCaseBriefJapanese, ...retiredTemplateLines]);

// A section still holding only the template's prompts has nothing written in it yet.
export function isTemplateOnly(body: string): boolean {
  return body
    .split("\n")
    .map((line) =>
      line
        .trim()
        .replace(/^[-*]\s+/, "")
        .replace(/\*\*|__/g, "")
    )
    .filter((line) => line && !line.startsWith("#"))
    .every((line) => !!placeholderRange(line, templateMatchers));
}

const COMPLETENESS_KEYS: Record<number, keyof CaseBriefCompleteness["sections"]> = {
  1: "decision",
  2: "context",
  3: "keyQuestions",
  4: "successCriteria",
  5: "assumptions",
  6: "constraints",
  7: "stakeholders"
};

// Items Leo took from the documents or inferred, which the user hasn't confirmed yet.
const UNCONFIRMED_MARKER = "— to confirm";

export function unconfirmedItems(body: string): number {
  return body.split("\n").filter((line) => line.includes(UNCONFIRMED_MARKER)).length;
}

// Template prompts left unanswered in a section that has something written in it.
export function unansweredPrompts(body: string): number {
  return body
    .split("\n")
    .map((line) =>
      line
        .trim()
        .replace(/^[-*]\s+/, "")
        .replace(/\*\*|__/g, "")
    )
    .filter((line) => line && !line.startsWith("#") && !!placeholderRange(line, templateMatchers)).length;
}

export function sectionFilled(completeness: CaseBriefCompleteness | null, number: number): boolean {
  const key = COMPLETENESS_KEYS[number];
  return !!(key && completeness?.sections?.[key]?.filled);
}
